/**
 * What each source actually returned.
 *
 * **Built since M2, and no screen ever showed it.** An observation is the raw record of one
 * collection attempt — which source, against what, whether it worked, and the digest of the
 * bytes that came back. It sits one level below the claims on the Results tab: a claim is
 * what an extractor made of an observation, and when a claim looks wrong this is where you
 * find out whether the source said something different or said nothing at all.
 *
 * The failure rows matter as much as the successes. A source that returned nothing and a
 * source that errored are indistinguishable from the Results tab, and they call for
 * completely different responses.
 *
 * **Triage reorders this list, and nothing more.** A model proposes which observations
 * repay reading first; the rows it does not rank stay exactly where they were, below the
 * ones it did. It makes no claim about anybody, nothing here is exportable, and an analyst
 * who ignores the ordering entirely sees the same evidence in a different sequence.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { GenerationAccepted, Observation, RankedObservation } from '@/lib/schemas';
import { Badge, Button, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The observations tab for one case. */
export function ObservationsTab({ caseId }: { caseId: string }): ReactElement {
  const canGenerate = useCan('generate_reports');
  const [awaiting, setAwaiting] = useState(false);

  const observations = useQuery({
    queryKey: ['observations', caseId],
    queryFn: () => request<Observation[]>(`/cases/${caseId}/observations`, { query: { limit: 200 } }),
  });

  const triage = useQuery({
    queryKey: ['triage', caseId],
    queryFn: () => request<RankedObservation[]>(`/cases/${caseId}/ai/triage`),
    // Poll only while an ordering is outstanding. Generation runs on the AI worker and
    // takes a minute or two against a local model.
    refetchInterval: awaiting ? 3000 : false,
  });

  const order = useMutation({
    mutationFn: () =>
      request<GenerationAccepted>(`/cases/${caseId}/ai/triage`, { method: 'POST' }),
    onSuccess: () => setAwaiting(true),
  });

  const ranked = triage.data ?? [];

  useEffect(() => {
    if (awaiting && ranked.length > 0) setAwaiting(false);
  }, [awaiting, ranked.length]);

  if (observations.isLoading) return <Loading />;
  if (observations.error) return <ErrorState error={observations.error} />;

  const byId = new Map(ranked.map((entry) => [entry.observation_id, entry]));
  // Ranked first, in the order proposed; everything else keeps its existing position
  // below. An observation the model did not rank is not thereby unimportant — it is
  // unranked, and hiding it would turn a suggestion into a filter.
  const rows = [...(observations.data ?? [])].sort((a, b) => {
    const left = byId.get(a.id)?.rank;
    const right = byId.get(b.id)?.rank;
    if (left === undefined && right === undefined) return 0;
    if (left === undefined) return 1;
    if (right === undefined) return -1;
    return left - right;
  });

  return (
    <div className="space-y-3">
      <p className="max-w-3xl text-xs text-muted">
        One row per collection attempt: what was asked, of which source, and what came back.
        A claim on the Results tab is an extractor's reading of one of these — so when a
        claim looks wrong, this is where you learn whether the source said something
        different or said nothing at all.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="triage-order"
          disabled={!canGenerate || awaiting || order.isPending}
          onClick={() => order.mutate()}
        >
          {awaiting || order.isPending ? 'Ordering…' : 'Order by what to read first'}
        </Button>
        {ranked.length > 0 && (
          <Badge tone="muted">{ranked.length} ranked</Badge>
        )}
        {awaiting && (
          <span className="text-xs text-muted" data-testid="triage-queued">
            Running on the AI worker. This reorders the list; it changes no evidence.
          </span>
        )}
        {order.error != null && <ErrorState error={order.error} />}
      </div>

      <Table
        headers={['Source', 'Query', 'Status', 'Collected', 'Evidence', 'Error']}
        empty="Nothing has been collected for this case yet."
        rows={rows.map((observation) => [
          <span key="p" className="font-mono text-xs" data-testid="observation-source">
            {observation.plugin_name}
            <span className="ml-1 text-muted">{observation.plugin_version}</span>
          </span>,
          <span key="q" className="text-xs text-muted">
            {observation.query_type}
          </span>,
          <Badge
            key="s"
            tone={
              observation.status === 'success'
                ? 'ok'
                : observation.status === 'empty'
                  ? 'muted'
                  : 'danger'
            }
          >
            {observation.status}
          </Badge>,
          <span key="t" className="text-xs text-muted">
            {observation.collected_at.slice(0, 19).replace('T', ' ')}
          </span>,
          // The provenance route: an observation's bytes are the evidence behind every
          // claim extracted from it.
          observation.evidence_sha256 ? (
            <Link
              key="e"
              to={`/cases/${caseId}/evidence?sha=${observation.evidence_sha256}`}
              className="font-mono text-xs text-accent"
            >
              {observation.evidence_sha256.slice(0, 12)}…
            </Link>
          ) : (
            <span key="e" className="text-xs text-muted">
              —
            </span>
          ),
          <span key="x" className="text-xs text-muted">
            {observation.error_class
              ? `${observation.error_class}: ${observation.error_detail ?? ''}`
              : '—'}
          </span>,
        ])}
      />
    </div>
  );
}
